import React, {useState} from 'react';

import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import CreatePartForm, {getCreateFormValidation} from 'molbio/parts/CreatePartForm';
import * as RegistryUtil from 'registry/RegistryUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

export interface CreatePartModalPayload {
  folderId: string;
  name: string;
  tagSchemaId?: string;
  partLinkFieldDbId: number | null;
}

export interface CreatePartModalProps {
  initialFolderId?: string;
  namePlaceholder?: string;
  initialPartLinkFieldDbId: number | null;
  partLinkFieldOptions: Array<OptionData> | null;
  onRequestClose: () => void;
  onRequestSubmit: (payload: CreatePartModalPayload) => void;
  displaySelectionStart: number;
  displaySelectionEnd: number;
}

export default function CreatePartModal(props: CreatePartModalProps): JSX.Element {
  const [name, setName] = useState<string>(props.namePlaceholder ?? '');
  const [folderId, setFolderId] = useState<Optional<string>>(
    props.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [partLinkFieldDbId, setPartLinkFieldDbId] = useState<number | null>(props.initialPartLinkFieldDbId);

  const {isValid} = getCreateFormValidation({name, folderId});

  const handleFolderIdChange = (newFolderId: string): void => {
    setFolderId(newFolderId);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
  };

  const handleSubmit = (): void => {
    if (folderId) {
      const {folderId: parsedFolderId} = RegistryUtil.parseFolderValue(folderId);
      // TODO: Implement tagSchemaId
      props.onRequestSubmit({folderId: parsedFolderId!, name, tagSchemaId: undefined, partLinkFieldDbId});
    }
    props.onRequestClose();
  };

  const description =
    'A new part will be created and this region ' +
    `(${props.displaySelectionStart}-${props.displaySelectionEnd}) ` +
    'will be replaced with it.';

  const buttons = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Replace',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: !isValid,
    },
  ];

  return (
    <Modal
      title="Replace selection with part"
      showHeaderBorder={false}
      enableRedesign={true}
      onRequestSubmit={handleSubmit}
      onRequestClose={props.onRequestClose}
      buttons={buttons}
    >
      <div className="u-small-padding-bottom">{description}</div>

      <CreatePartForm
        name={name}
        folderId={folderId}
        partLinkFieldDbId={partLinkFieldDbId}
        partLinkFieldOptions={props.partLinkFieldOptions}
        onNameChange={setName}
        onFolderIdChange={handleFolderIdChange}
        onPartLinkFieldDbIdChange={setPartLinkFieldDbId}
      />
    </Modal>
  );
}
